import React from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { getPolicy, PolicyBlock } from '../content/policies';
import NotFoundPage from './NotFoundPage';
import useDocumentTitle from '../hooks/useDocumentTitle';

const bodySx = { mb: 2, lineHeight: 1.75, whiteSpace: 'pre-line' as const };

const renderBlock = (block: PolicyBlock, index: number) => {
  if (block.type === 'heading') {
    return (
      <Typography key={index} variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
        {block.text}
      </Typography>
    );
  }
  if (block.type === 'list') {
    return (
      <Box key={index} component="ul" sx={{ pl: 3, mb: 2, mt: 0 }}>
        {block.items.map((item, i) => (
          <Box key={i} component="li" sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Typography key={index} variant="body2" color="text.secondary" sx={bodySx}>
      {block.text}
    </Typography>
  );
};

const PolicyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const policy = getPolicy(slug || '');

  useDocumentTitle(policy?.title, policy ? `${policy.title} for vediluxe.com` : undefined);

  if (!policy) {
    return <NotFoundPage />;
  }

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
        <Typography variant="h4" sx={{ mb: 0.5 }}>
          {policy.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Last updated: {policy.updated}
        </Typography>
        <Divider sx={{ my: 2.5 }} />

        {policy.blocks
          ? policy.blocks.map(renderBlock)
          : (policy.paragraphs || []).map((text, index) => (
              <Typography key={index} variant="body2" color="text.secondary" sx={bodySx}>
                {text}
              </Typography>
            ))}
      </Paper>
    </Box>
  );
};

export default PolicyPage;
